import { Button, Input, MultiSelect, Textarea } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation, useQuery } from "@tanstack/react-query";
import Common from "@utilities/common";
import { Form, Formik } from "formik";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IFormData } from "./types";
import { Schema } from "./validations";

interface Props {
    data: IGalleryItem["get"];
}

const FormContainer: FC<Props> = ({ data }) => {
    const [initialValues, setInitialValues] = useState<IFormData>({
        categories: [],
        products: [],
        description: "",
        title: "",
    });
    const [categoriesQuery, setCategoriesQuery] = useState("");

    const Navigate = useNavigate();

    const { data: categoriesData } = useQuery(
        [
            "getCategories",
            {
                query: categoriesQuery,
            },
        ],
        () =>
            Common.api.categories.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { data: productsData } = useQuery(
        [
            "getProducts",
            {
                query: "",
            },
        ],
        () => Common.api.products.GetAllWithoutFilter().then((res) => res.data),
        {
            retry: false,
        },
    );

    const { mutate, isLoading } = useMutation(
        ({ description, products, categories, title }: IFormData) =>
            Common.api.gallery.Edit({
                id: data.id,
                title,
                products,
                categories,
                ...(description ? { description } : {}),
            }),
        {
            onSuccess: () => {
                Navigate(RoutesInstance.gallery);
            },
        },
    );

    useEffect(() => {
        setInitialValues({
            title: data.title,
            products: data.products.map((item) => item.id),
            categories: data.categories.map((item) => item.id),
            description: data?.description || "",
        });
    }, [data]);

    const handleSubmit = async (values: IFormData) => {
        mutate(values);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={Schema}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize>
            {({ values, errors, handleChange, handleBlur, isValid }) => (
                <Form>
                    <div className="mb-6">
                        <img className="w-96 block mx-auto" src={data.file} />
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="title"
                            label="عنوان"
                            required
                            error={errors.title}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="title"
                                name="title"
                                type="text"
                                placeholder="عنوان"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.title}
                                invalid={!!errors.title}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <MultiSelect
                            variant="filled"
                            data={
                                categoriesData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []
                            }
                            value={values.categories.map((item) =>
                                item.toString(),
                            )}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "categories",
                                        name: "categories",
                                        value: value.map((item) => +item),
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            label="دسته بندی ها"
                            placeholder="انتخاب دسته بندی"
                            searchable
                            searchValue={categoriesQuery}
                            onSearchChange={(query) =>
                                setCategoriesQuery(query)
                            }
                            nothingFound="موردی یافت نشد"
                        />
                    </div>

                    <div className="mb-4">
                        <MultiSelect
                            variant="filled"
                            data={
                                productsData?.result?.map((item) => ({
                                    value: item.id.toString(),
                                    label: item.faName,
                                })) || []
                            }
                            value={values.products.map((item) =>
                                item.toString(),
                            )}
                            onChange={(value) =>
                                handleChange({
                                    target: {
                                        id: "products",
                                        name: "products",
                                        value: value.map((item) => +item),
                                    },
                                })
                            }
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            label="محصولات"
                            placeholder="انتخاب محصول"
                            searchable
                            nothingFound="موردی یافت نشد"
                        />
                    </div>

                    <div className="mb-10">
                        <Textarea
                            variant="filled"
                            color="blue"
                            id="description"
                            name="description"
                            placeholder="توضیحات"
                            label="توضیحات"
                            minRows={10}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            error={errors.description}
                            value={values.description}
                        />
                    </div>

                    <div className="text-center">
                        <Button
                            variant="filled"
                            className="transition-all duration-300 px-16"
                            color="blue"
                            radius={9999}
                            type="submit"
                            disabled={!isValid || isLoading}
                            loading={isLoading}>
                            ثبت
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default FormContainer;
